import { Form, Formik } from 'formik';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { withQueryParams } from '@shared/commonFunctions';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { Modal } from '@shared/components/modals/Modal';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { setLastCommentsRefresh } from '@store/reducers/contactManagerSlice';
import { footerLenderSelector, setFooterLenderAction } from '@store/reducers/dealCalculatorSlice';

import { DealCalculatorFooter } from './components/DealCalculatorFooter';
import { DealCalculatorFilters } from './components/DealCalculatorFilters';
import { DealCalculatorHeader } from './components/DealCalculatorHeader';
import { DealCalculatorLenders } from './components/DealCalculatorLenders/DealCalculatorLenders';
import { requestGetDealCalculatorData, requestSaveDealCalculator } from './components/queries';
import { IDealCalculatorProps, ILenderFooterState, ILendersResponse, ISaveDealCaldData as ISaveDealCalcData } from './interfaces';
import { DealCalculatorModel } from './models/DealCalculatorModel';
import { IEligibleLender } from './models/EligibleLenderModel';
import { dealCalculatorFormSchema } from './validation';
import { GetDealCalculatorModel } from './models/InputsModel';

export const DealCalculator = ({
  data: request,
  handleClosePopUp,
  refreshDeals,
  refreshLeads,
  reloadDeal,
  assetNumericId,
  numericId,
  isVoidEnable,
  contactAddress,
  reloadContactAlerts,
  ...other
}: IDealCalculatorProps) => {
  const requestConfig: GetDealCalculatorModel = {
    dealId: request.dealId,
    contactId: request.contactId,
    isDealEdit: request.isDealEdit,
    isEstimatedScore: request.isEstimatedScore,
    estimatedScore: request.estimatedScore,
  };

  const leadId = request.leadId;

  const { isAdmin } = useUserAccount();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [lendersData, setLendersData] = useState<ILendersResponse>();
  const [initialState, setInitialState] = useState<DealCalculatorModel>({} as DealCalculatorModel);
  const [shouldClose, setShouldClose] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openIsChangeModal, setOpenIsChangeModal] = useState(false);
  const [newDealId, setNewDealId] = useState<string | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formRef = useRef<any>();

  const getDealCalculatorData = useCallback(async () => {
    setLoading(true);
    const data = await requestGetDealCalculatorData(leadId ?? '', requestConfig);

    if (data) {
      setLendersData(data);
      setInitialState(new DealCalculatorModel(data));
      dispatch(
        setFooterLenderAction({
          additionalNotes: data.globalInputs?.additionalNotes,
        })
      );
    }
    setLoading(false);
  }, [leadId, requestConfig]);

  useEffect(() => {
    getDealCalculatorData();
  }, []);

  const footerLender: Partial<ILenderFooterState> | null = useSelector(footerLenderSelector);

  const onClose = useCallback(() => {
    dispatch(
      setFooterLenderAction({
        additionalNotes: null,
      })
    );
    handleClosePopUp?.();

    if (newDealId) {
      navigate(withQueryParams('.', { dealId: newDealId }));
      refreshTable();
      setNewDealId(null);
    }
  }, [newDealId]);

  const handleSubmit = useCallback(
    async (form: ILendersResponse) => {
      let resp;
      const postData: ISaveDealCalcData = {
        globalInputs: {
          salesPrice: form.globalInputs?.salesPrice ?? 0,
          cashOut: form.globalInputs?.cashOut ?? 0,
          downPayment: form.globalInputs?.downPayment ?? 0,
          rateMarkup: form.globalInputs?.rateMarkup ?? 0,
          term: form.globalInputs?.term ?? 0,
          additionalNotes: footerLender?.additionalNotes ?? '',
          isAch: form.globalInputs?.isAch ?? false,
          coContactId: form.globalInputs?.coContactId,
        },
        lenderInputs: form.eligibleLenders?.map((item: IEligibleLender) => ({
          lenderCode: item?.code,
          cashOut: item.inputs?.cashOut ?? 0,
          downPayment: item.inputs?.downPayment ?? 0,
          rateMarkup: item.inputs?.rateMarkup ?? 0,
          term: item.inputs?.term ?? 0,
          approvedRate: item.inputs?.approvedRate ?? 0,
          vscRetail: item.inputs?.vscRetail ?? 0,
          mbiRetail: item.inputs?.mbiRetail ?? 0,
          gapRetail: item.inputs?.gapRetail ?? 0,
          comRetail: item.inputs?.comRetail ?? 0,
          pafRetail: item.inputs?.pafRetail ?? 0,
          vrpRetail: item.inputs?.vrpRetail ?? 0,
          totalDebt: item.inputs?.totalDebt ?? 0,
          totalUnsecuredDebt: item.inputs?.totalUnsecuredDebt ?? 0,
          firstPaymentDays: item.inputs?.firstPaymentDays ?? 0,
          contractDate: item.inputs?.contractDate ?? new Date().toString(),
          rateAdjustment: item.inputs?.rateAdjustment ?? 0,
          isAch: item.inputs?.isAch ?? false,
          isDeclined: item.inputs?.isDeclined ?? false,
        })),
      };
      setLoading(true);
      if (!postData?.lenderInputs) {
        Notistack.generalError('The LenderInputs field is required');
        resp = null;
      } else {
        resp = leadId && (await requestSaveDealCalculator(postData, leadId));
        if (resp) {
          await getDealCalculatorData();
          reloadDeal?.();
          !newDealId && refreshDeals?.();
          refreshLeads?.();
        }
      }
      shouldClose && resp && onClose();
      setShouldClose(false);
      dispatch(setLastCommentsRefresh(new Date().toISOString()));
      setLoading(false);
    },
    [shouldClose, onClose, footerLender?.additionalNotes, newDealId]
  );

  const handleSubmitCalculator = (withClosingModal = false) => {
    if (formRef.current) {
      setOpenIsChangeModal(false);
      withClosingModal && setShouldClose(true);
      formRef.current.handleSubmit();
    }
  };

  const handleClose = useCallback(() => {
    if (!formRef.current?.dirty) {
      onClose();
    } else {
      setOpenIsChangeModal(true);
    }
  }, [formRef.current?.dirty, onClose]);

  const closeAlertModal = useCallback(() => {
    setOpenIsChangeModal(false);
    onClose();
  }, [onClose]);

  const refreshTable = useCallback(async () => {
    await refreshDeals?.();
    await refreshLeads?.();
  }, []);

  return (
    <Modal
      {...other}
      title="Deal Calculator"
      dialogWidth="100%"
      footer={
        <DealCalculatorFooter
          handleSubmitCalculator={handleSubmitCalculator}
          loading={loading}
          globalInputs={lendersData?.globalInputs}
        />
      }
      onClose={handleClose}
    >
      <LoaderBackdrop open={loading} />

      <DealCalculatorHeader headerData={lendersData?.header} />

      <Formik
        enableReinitialize
        innerRef={formRef}
        initialValues={initialState as DealCalculatorModel}
        validationSchema={dealCalculatorFormSchema}
        onSubmit={handleSubmit}
      >
        {(props) => (
          <Form onSubmit={props.handleSubmit}>
            {openIsChangeModal && (
              <AlertModal
                open
                title="Unsaved Alert"
                subtitle="You have unsaved changes, do you want to save?"
                buttonText="Save changes"
                cancelButtonText="Discard changes"
                onConfirm={() => handleSubmitCalculator(true)}
                onClose={closeAlertModal}
              />
            )}

            <DealCalculatorFilters
              assetId={lendersData?.header?.assetId || ''}
              leadId={leadId}
              dealId={request.dealId}
              assetNumericId={assetNumericId}
              numericId={numericId}
              isVoidEnable={isVoidEnable}
              globalInputs={props.values.globalInputs}
              setFieldValue={props.setFieldValue}
              saveAndPullEligibleLenders={() => handleSubmitCalculator(false)}
              onClose={handleClose}
            />

            <DealCalculatorLenders
              isVoidEnable={isAdmin || isVoidEnable || false}
              assetId={lendersData?.header?.assetId || ''}
              formProps={props}
              leadId={leadId}
              dealId={request.dealId}
              contactAddress={contactAddress}
              coContactId={props.values.globalInputs?.coContactId}
              setFieldValue={props.setFieldValue}
              setLoading={setLoading}
              setNewDealId={setNewDealId}
              reloadDealCalculator={getDealCalculatorData}
              reloadContactAlerts={reloadContactAlerts}
            />
          </Form>
        )}
      </Formik>
    </Modal>
  );
};
